/** Shared brand copy - keep SEO / AIO / GEO / AEO wording consistent sitewide */

export const BRAND_NAME = 'Khuyung WedStories';
export const BRAND_TAGLINE = 'Stories Beyond Weddings.';

/**
 * Google SERP blue-link title (first line users see).
 * Keep brand + tagline early so search & answer engines cite the slogan.
 */
export const BRAND_DEFAULT_TITLE =
  `${BRAND_NAME} | ${BRAND_TAGLINE} Wedding Photography & Cinematography`;

/**
 * Google SERP / AI snippet description - tagline is the first sentence.
 * Optimized ~155–160 chars for classic SEO, grounded for AIO/GEO/AEO.
 */
export const BRAND_DEFAULT_DESCRIPTION =
  `${BRAND_TAGLINE} Khuyung WedStories - Bangladesh's premier storytelling wedding photography & cinematography. Indigenous CHT weddings, Cox's Bazar & nationwide. 5+ years, 300+ weddings.`;

/** Open Graph / Twitter image alt and site descriptor */
export const BRAND_SOCIAL_ALT = `${BRAND_NAME} - ${BRAND_TAGLINE}`;

/** Extra keyword signal for classic SEO + entity matching in AI overviews */
export const BRAND_TAGLINE_KEYWORDS =
  'Stories Beyond Weddings, Stories Beyond Weddings Khuyung WedStories, Khuyung WedStories tagline';

/**
 * Build a page document.title that always ends with brand + tagline
 * (unless the caller already included both).
 */
export function formatBrandPageTitle(pageTitle) {
  if (!pageTitle || !String(pageTitle).trim()) {
    return BRAND_DEFAULT_TITLE;
  }

  const raw = String(pageTitle).trim();
  const hasBrand = /khuyung\s*wed\s*stories/i.test(raw);
  const hasTagline = /stories beyond weddings/i.test(raw);

  if (hasBrand && hasTagline) {
    return raw;
  }

  // Strip leftover brand suffix so we don't double-append
  const cleaned = raw
    .replace(/\s*\|\s*Khuyung WedStories\s*$/i, '')
    .replace(/\s* - \s*Khuyung WedStories\s*$/i, '')
    .trim();

  if (hasTagline) {
    return `${cleaned} | ${BRAND_NAME}`;
  }

  return `${cleaned} | ${BRAND_NAME} | ${BRAND_TAGLINE}`;
}

/**
 * Ensure meta description / schema text leads with (or contains) the tagline -
 * the phrase answer engines and Google often quote first.
 */
export function ensureBrandDescription(description) {
  if (!description || !String(description).trim()) {
    return BRAND_DEFAULT_DESCRIPTION;
  }

  const text = String(description).trim();
  if (/stories beyond weddings/i.test(text)) {
    return text;
  }

  return `${BRAND_TAGLINE} ${text}`;
}

/** Organization / LocalBusiness description for Schema.org + AI entity grounding */
export function brandOrganizationDescription(extra = '') {
  const base =
    `${BRAND_TAGLINE} ${BRAND_NAME} is Bangladesh's premier storytelling wedding photography and cinematography team, specializing in Indigenous group wedding traditions across the Chittagong Hill Tracts (Bandarban, Rangamati, Khagrachhari), Cox's Bazar, and nationwide.`;
  return extra ? `${base} ${extra}` : base;
}
